// src/admin/pages/report/reportChartRenderer.tsx
//
// Renders the same charts shown in ReportDetailsDialog.tsx off-screen (in a
// hidden, fixed-size container) and captures each one as a PNG via
// html2canvas, so ReportPDFGenerator can embed them in the downloaded PDF.
// Previously the PDF only ever contained text/tables — the charts existed
// only in the web dialog.
import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Report } from './Report';

// Same palette as ReportDetailsDialog.tsx, duplicated here so this module
// stays independent (the dialog's chart components aren't exported).
const CHART_COLORS = ['#4db6ac', '#5c6bc0', '#ffb74d', '#ba68c8', '#4fc3f7', '#e57373', '#81c784', '#f06292', '#9575cd', '#a1887f'];

// Fixed capture size — off-screen rendering doesn't need ResponsiveContainer
// or breakpoints, just one consistent size to rasterize at.
const CAPTURE_WIDTH = 900;
const CAPTURE_HEIGHT = 400;

export interface CapturedChart {
  title: string;
  dataUrl: string;
  pixelWidth: number;
  pixelHeight: number;
}

const compactNum = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};

interface SeriesSpec { key: string; name: string; color?: string; }

const StaticBarChart: React.FC<{ data: any[]; xKey: string; bars: SeriesSpec[]; sym: string; currency?: boolean }> = ({ data, xKey, bars, sym, currency }) => (
  <BarChart width={CAPTURE_WIDTH} height={CAPTURE_HEIGHT} data={data} margin={{ top: 16, right: 24, left: 8, bottom: 24 }}>
    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
    <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: '#555' }} interval={0} angle={-20} textAnchor="end" height={60} />
    <YAxis tick={{ fontSize: 12, fill: '#555' }} tickFormatter={compactNum} width={56} />
    <Tooltip formatter={(v: any) => (currency ? `${sym} ${Number(v).toLocaleString()}` : Number(v).toLocaleString())} />
    {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 13 }} />}
    {bars.map((b, i) => (
      <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color || CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
    ))}
  </BarChart>
);

const StaticPieChart: React.FC<{ data: any[]; nameKey: string; valueKey: string; sym: string }> = ({ data, nameKey, valueKey, sym }) => (
  <PieChart width={CAPTURE_WIDTH} height={CAPTURE_HEIGHT}>
    <Pie data={data} dataKey={valueKey} nameKey={nameKey} cx="38%" cy="50%" outerRadius={140} innerRadius={70} paddingAngle={2} isAnimationActive={false}>
      {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
    </Pie>
    <Tooltip formatter={(v: any) => `${sym} ${Number(v).toLocaleString()}`} />
    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 13 }} />
  </PieChart>
);

const StaticLineChart: React.FC<{ data: any[]; xKey: string; lines: SeriesSpec[]; sym: string; currency?: boolean; dashed?: string[] }> = ({ data, xKey, lines, sym, currency, dashed }) => (
  <LineChart width={CAPTURE_WIDTH} height={CAPTURE_HEIGHT} data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
    <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: '#555' }} interval="preserveStartEnd" />
    <YAxis tick={{ fontSize: 12, fill: '#555' }} tickFormatter={compactNum} width={56} />
    <Tooltip formatter={(v: any) => (currency ? `${sym} ${Number(v).toLocaleString()}` : Number(v).toLocaleString())} />
    <Legend wrapperStyle={{ fontSize: 13 }} />
    {lines.map((l, i) => (
      <Line
        key={l.key} type="monotone" dataKey={l.key} name={l.name}
        stroke={l.color || CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false}
        isAnimationActive={false} connectNulls={false}
        strokeDasharray={dashed?.includes(l.key) ? '5 4' : undefined}
      />
    ))}
  </LineChart>
);

const StaticAreaChart: React.FC<{ data: any[]; xKey: string; areas: SeriesSpec[]; sym: string }> = ({ data, xKey, areas, sym }) => (
  <AreaChart width={CAPTURE_WIDTH} height={CAPTURE_HEIGHT} data={data} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
    <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: '#555' }} />
    <YAxis tick={{ fontSize: 12, fill: '#555' }} tickFormatter={compactNum} width={56} />
    <Tooltip formatter={(v: any) => `${sym} ${Number(v).toLocaleString()}`} />
    <Legend wrapperStyle={{ fontSize: 13 }} />
    {areas.map((a, i) => (
      <Area
        key={a.key} type="monotone" dataKey={a.key} name={a.name}
        stroke={a.color || CHART_COLORS[i % CHART_COLORS.length]} fill={a.color || CHART_COLORS[i % CHART_COLORS.length]}
        fillOpacity={0.25} isAnimationActive={false}
      />
    ))}
  </AreaChart>
);

// ── Build the list of chart elements for a report (mirrors ReportDetailsDialog.tsx exactly) ──
const buildChartJobs = (report: Report, sym: string): { title: string; element: React.ReactElement }[] => {
  const data: any = report.data || {};
  const jobs: { title: string; element: React.ReactElement }[] = [];

  switch (report.reportType) {
    case 'sales': {
      const top = data.summary?.topProducts;
      if (top?.length > 0) {
        jobs.push({
          title: 'Top Products by Revenue',
          element: <StaticBarChart data={top.slice(0, 8).map((p: any) => ({ name: p.productName, revenue: p.revenue }))} xKey="name" sym={sym} currency bars={[{ key: 'revenue', name: 'Revenue', color: CHART_COLORS[0] }]} />,
        });
        jobs.push({
          title: 'Top Products Share',
          element: <StaticPieChart data={top.slice(0, 6).map((p: any) => ({ name: p.productName, revenue: p.revenue }))} nameKey="name" valueKey="revenue" sym={sym} />,
        });
      }
      break;
    }

    case 'purchase_orders': {
      const statusBreakdown = data.summary?.statusBreakdown;
      if (statusBreakdown && Object.keys(statusBreakdown).length > 0) {
        jobs.push({
          title: 'POs by Status',
          element: <StaticPieChart data={Object.entries(statusBreakdown).map(([name, value]) => ({ name, value: value as number }))} nameKey="name" valueKey="value" sym={sym} />,
        });
      }
      const topSuppliers = data.summary?.topSuppliers;
      if (topSuppliers?.length > 0) {
        jobs.push({
          title: 'Top Suppliers by Spend',
          element: <StaticBarChart data={topSuppliers.slice(0, 8).map((s: any) => ({ name: s.supplierName, amount: s.totalAmount || s.totalPurchased || 0 }))} xKey="name" sym={sym} currency bars={[{ key: 'amount', name: 'Amount', color: CHART_COLORS[2] }]} />,
        });
      }
      break;
    }

    case 'product_performance': {
      if (data.products?.length > 0) {
        jobs.push({
          title: 'Top Products by Revenue',
          element: (
            <StaticBarChart
              data={[...data.products].sort((a: any, b: any) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 10).map((p: any) => ({ name: p.productName, revenue: p.revenue }))}
              xKey="name" sym={sym} currency bars={[{ key: 'revenue', name: 'Revenue', color: CHART_COLORS[0] }]}
            />
          ),
        });
      }
      break;
    }

    case 'revenue': {
      if (data.dailyRevenue?.length > 0) {
        jobs.push({
          title: 'Daily Revenue Trend',
          element: (
            <StaticLineChart
              data={data.dailyRevenue.map((d: any) => ({
                date: d.date instanceof Date ? d.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : String(d.date),
                revenue: d.totalRevenue, net: d.netRevenue,
              }))}
              xKey="date" sym={sym} currency
              lines={[{ key: 'revenue', name: 'Revenue', color: CHART_COLORS[0] }, { key: 'net', name: 'Net Revenue', color: CHART_COLORS[1] }]}
            />
          ),
        });
      }
      if (data.topCategories?.length > 0) {
        jobs.push({ title: 'Revenue by Category', element: <StaticPieChart data={data.topCategories.slice(0, 8)} nameKey="categoryName" valueKey="revenue" sym={sym} /> });
      }
      break;
    }

    case 'returns_cancellations': {
      const s = data.summary;
      if (s) {
        jobs.push({
          title: 'Returned vs Cancelled vs Refunded',
          element: (
            <StaticBarChart
              data={[
                { name: 'Returned', value: s.totalReturned },
                { name: 'Cancelled', value: s.totalCancelled },
                { name: 'Refunded', value: s.totalRefunded },
              ]}
              xKey="name" sym={sym} bars={[{ key: 'value', name: 'Orders', color: CHART_COLORS[5] }]}
            />
          ),
        });
        if (s.topReturnedProducts?.length > 0) {
          jobs.push({
            title: 'Top Returned Products (Lost Revenue)',
            element: <StaticBarChart data={s.topReturnedProducts.slice(0, 6).map((p: any) => ({ name: p.productName, revenue: p.revenue }))} xKey="name" sym={sym} currency bars={[{ key: 'revenue', name: 'Lost Revenue', color: CHART_COLORS[3] }]} />,
          });
        }
      }
      break;
    }

    case 'profit_loss': {
      const s = data.summary;
      if (s) {
        jobs.push({
          title: 'Revenue → Profit Waterfall',
          element: (
            <StaticBarChart
              data={[
                { name: 'Revenue', value: s.revenue },
                { name: 'COGS', value: s.cogs },
                { name: 'Gross Profit', value: s.grossProfit },
                { name: 'Op. Costs', value: s.operatingCosts },
                { name: 'Net Profit', value: s.netProfit },
              ]}
              xKey="name" sym={sym} currency bars={[{ key: 'value', name: 'Amount', color: CHART_COLORS[1] }]}
            />
          ),
        });
      }
      if (data.costBreakdown?.length > 0) {
        jobs.push({ title: 'Operating Costs by Category', element: <StaticPieChart data={data.costBreakdown} nameKey="category" valueKey="amount" sym={sym} /> });
      }
      break;
    }

    case 'cost': {
      const timeline = data.costTimeline || [];
      if (timeline.length > 0) {
        jobs.push({
          title: 'Cost Trend by Month',
          element: (
            <StaticBarChart
              data={timeline} xKey="period" sym={sym} currency
              bars={[{ key: 'operatingCosts', name: 'Operating', color: CHART_COLORS[1] }, { key: 'purchaseCosts', name: 'Purchases', color: CHART_COLORS[2] }]}
            />
          ),
        });
      }
      const typeBreakdown = (data.costTypeBreakdown || []).filter((x: any) => x.amount > 0);
      if (typeBreakdown.length > 0) {
        jobs.push({ title: 'Cost Composition', element: <StaticPieChart data={typeBreakdown} nameKey="label" valueKey="amount" sym={sym} /> });
      }
      if (data.costByCategory?.length > 0) {
        jobs.push({
          title: 'Operating Cost by Category',
          element: <StaticBarChart data={data.costByCategory.slice(0, 8)} xKey="category" sym={sym} currency bars={[{ key: 'amount', name: 'Amount', color: CHART_COLORS[0] }]} />,
        });
      }
      if (data.costBySupplier?.length > 0) {
        jobs.push({
          title: 'Purchase Spend by Supplier',
          element: (
            <StaticBarChart
              data={data.costBySupplier.slice(0, 8)} xKey="supplierName" sym={sym} currency
              bars={[{ key: 'totalPurchased', name: 'Purchased', color: CHART_COLORS[3] }, { key: 'outstanding', name: 'Outstanding', color: CHART_COLORS[5] }]}
            />
          ),
        });
      }
      break;
    }

    case 'payments': {
      if (data.paymentTimeline?.length > 0) {
        jobs.push({
          title: 'Received vs. Paid Out by Month',
          element: (
            <StaticAreaChart
              data={data.paymentTimeline} xKey="period" sym={sym}
              areas={[{ key: 'received', name: 'Received', color: CHART_COLORS[0] }, { key: 'paidOut', name: 'Paid Out', color: CHART_COLORS[5] }]}
            />
          ),
        });
      }
      if (data.incomingByMethod?.length > 0) {
        jobs.push({ title: 'Payments Received by Method', element: <StaticPieChart data={data.incomingByMethod} nameKey="method" valueKey="amount" sym={sym} /> });
      }
      break;
    }

    case 'forecast': {
      const historicalRows = (data.historicalData?.dataPoints || []).map((p: any) => ({
        date: p.date instanceof Date ? p.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : String(p.date),
        actual: p.value,
      }));
      const predictedRows = (data.predictions || []).map((p: any) => ({
        date: p.date instanceof Date ? p.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : String(p.date),
        predicted: p.predictedValue,
      }));
      const chartData = [...historicalRows, ...predictedRows];
      if (chartData.length > 0) {
        jobs.push({
          title: 'Historical vs. Predicted',
          element: (
            <StaticLineChart
              data={chartData} xKey="date" sym={sym} currency={data.forecastType === 'revenue'}
              lines={[{ key: 'actual', name: 'Historical', color: CHART_COLORS[0] }, { key: 'predicted', name: 'Predicted', color: CHART_COLORS[2] }]}
              dashed={['predicted']}
            />
          ),
        });
      }
      break;
    }

    default:
      break;
  }

  return jobs;
};

// ── Off-screen render + capture ──────────────────────────────────────────────
// Renders each chart into a hidden, fixed-size container (off the visible
// viewport, not display:none — html2canvas can't rasterize display:none
// elements), waits a couple of frames for recharts/SVG to paint, then
// captures a PNG via html2canvas. Cleans up after itself either way.
export const captureReportCharts = async (report: Report, sym: string): Promise<CapturedChart[]> => {
  const jobs = buildChartJobs(report, sym);
  if (jobs.length === 0) return [];

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-10000px';
  container.style.left = '-10000px';
  container.style.zIndex = '-1';
  container.style.background = '#ffffff';
  document.body.appendChild(container);

  const results: CapturedChart[] = [];

  try {
    for (const job of jobs) {
      const holder = document.createElement('div');
      holder.style.width = `${CAPTURE_WIDTH}px`;
      holder.style.height = `${CAPTURE_HEIGHT}px`;
      holder.style.background = '#ffffff';
      container.appendChild(holder);

      const root = createRoot(holder);
      root.render(job.element);

      // Two rAFs let recharts complete its render/measure cycle; the extra
      // timeout is a small safety margin for the SVG paint to actually flush.
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      await new Promise((resolve) => setTimeout(resolve, 120));

      try {
        const canvas = await html2canvas(holder, { backgroundColor: '#ffffff', scale: 2, logging: false });
        results.push({
          title: job.title,
          dataUrl: canvas.toDataURL('image/png'),
          pixelWidth: canvas.width,
          pixelHeight: canvas.height,
        });
      } catch (err) {
        console.error(`captureReportCharts: failed to capture "${job.title}"`, err);
      } finally {
        root.unmount();
      }
    }
  } finally {
    document.body.removeChild(container);
  }

  return results;
};
